import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

// The three placings a league awards, and their artwork.
//
// Shared on purpose: the badges shown while CREATING a league and the badges
// shown on its landing page have to be the same three, or the promise made
// during setup won't match what a joiner is told they're playing for.
//
// Client (badge asset drop): the captions under the art are now the placing
// only — "1st / 2nd / 3rd". The badge's NAME ("THE W", "SO CLOSE", "PLACED")
// and its scarcity line ("1 OF 1 EVER", "PUBLIC LEAGUE") are printed on the
// artwork itself, so repeating them underneath would say the same thing twice.
export const PODIUM_BADGES = [
  { key: "the-w", caption: "1st", placement: "1st place" },
  { key: "so-close", caption: "2nd", placement: "2nd place" },
  { key: "placed", caption: "3rd", placement: "3rd place" },
];

// A competition type maps onto the badge set drawn for it. The client supplied
// one PNG per placing per type, in /public, with the competition's own wording
// ("CASH GAME" / "PUBLIC LEAGUE" / "PRIVATE LEAGUE") baked into the image rather
// than overlaid in CSS.
//
// `variantKey` is "cash-game" | "public" | "private" — a league's `game_type`
// maps straight onto the last two, and a cash game takes the first.
const BADGE_ART_PREFIX = {
  "cash-game": "cash-game",
  public: "public-league",
  private: "private-league",
};

export const podiumBadgeSrc = (variantKey, badgeKey) =>
  `/${BADGE_ART_PREFIX[variantKey] || BADGE_ART_PREFIX.public}-badge-${badgeKey}-landing.png`;

export const PodiumBadge = ({
  badgeKey,
  caption,
  variantKey = "public",
  placement,
  onOpen,
}) => {
  // Client: "Tap any badge on mobile landing pages. Display that badge enlarged
  // in the center of the screen... No pinch, swipe, or separate screen is
  // needed."
  //
  // MOBILE ONLY, and the gate is CSS rather than a width read in JS. A
  // `window.innerWidth` test would have to pick a value during the first render
  // — before the browser has laid anything out — and then re-pick it on every
  // resize; between those two moments the badge would be tappable at a width
  // where the client did not ask for it. `md:pointer-events-none` cannot
  // disagree with the width it is being read at, and it takes the artwork with
  // it, since pointer-events is inherited by the <img> inside.
  const art = (
    <img
      src={podiumBadgeSrc(variantKey, badgeKey)}
      alt=""
      width="1254"
      height="1254"
      loading="lazy"
      decoding="async"
      className="w-full h-auto aspect-square object-contain select-none"
    />
  );

  return (
    <div className="flex flex-col items-center text-center">
      {onOpen ? (
        <button
          type="button"
          onClick={() => onOpen(badgeKey)}
          aria-label={`View the ${placement || caption} badge`}
          className="block w-full md:pointer-events-none"
        >
          {art}
        </button>
      ) : (
        art
      )}
      <span className="mt-2 text-xs text-[#1A1A1A]">{caption}</span>
    </div>
  );
};

// The enlarged badge. Client, in full: "Display that badge enlarged in the
// center of the screen. Add a subtle dark overlay behind it. Tap the badge or
// anywhere outside it to close. Include the placement below it, such as 1st
// place. Keep the user on the same scroll position after closing."
//
// So: one scrim that IS the close control (tapping the badge lands on it too,
// because the art sits inside the same button), the placement under the art, and
// nothing else — no caption, no chrome, no gestures.
//
// The scroll position is kept by NOT touching it. The usual way to stop the page
// scrolling behind a modal is `position: fixed` on <body>, which collapses the
// document to the viewport and returns the reader to the top of the page when it
// is undone unless the offset is saved and restored by hand — the exact thing
// the client asked not to happen. Here the scrim covers the viewport, so there
// is nothing behind it to touch, and `overscroll-contain` + `touch-action: none`
// stop a drag on the scrim itself from chaining through to the page. The
// document is never moved, so there is nothing to restore.
//
// Escape closing is ours, not the client's: this is a modal dialog and every
// other one in the app (the Switch competition sheet, the create-league modal)
// closes on Escape. It has no effect on a phone, which is the surface the client
// specified.
const PodiumBadgeLightbox = ({ variantKey, badge, onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!badge) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${badge.placement} badge`}
      onClick={onClose}
      style={{ touchAction: "none" }}
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center overscroll-contain bg-black/70 px-8 md:hidden"
    >
      <img
        src={podiumBadgeSrc(variantKey, badge.key)}
        alt=""
        width="1254"
        height="1254"
        decoding="async"
        /* "as big as possible within the allotted space" was the rule for the
           card; here the space is the screen. 80vw is the badge's natural limit
           on a phone held upright, and 60vh keeps it whole on a short screen —
           whichever runs out first wins, so the art is never cropped and never
           overlaps the placement line. */
        /* Client, on the edges: "The expanded state should render a separate
           image at its final width using object-fit: contain and
           image-rendering: auto, rather than enlarging the small card image with
           transform: scale()."

           It already does render a SEPARATE <img> at its own size rather than
           scaling the card's copy, and it carries no filter, drop-shadow,
           box-shadow or transform — there is nothing here to remove. The one
           thing that was implicit is `image-rendering`, so it is now stated
           outright: the app's stylesheet sets `crisp-edges` on chart canvases,
           and spelling `auto` here means this image can never inherit a
           nearest-neighbour rule if that selector is ever widened. */
        style={{ imageRendering: "auto" }}
        className="max-h-[60vh] w-[80vw] max-w-sm object-contain select-none"
      />
      <p className="mt-5 text-base font-semibold text-white">{badge.placement}</p>
    </div>,
    document.body
  );
};

// The two places this card appears word it differently, and the client gave the
// copy for each verbatim. Landing pages pitch the badges to someone deciding
// whether to enter; the create wizard states what the league will award.
export const PODIUM_COPY = {
  landing: {
    heading: "Build your case",
    subheading: "Top 3 finishes add to your trophy case.",
    footnote: "One badge per podium place, per competition. Earned, not claimed.",
  },
  creation: {
    heading: "Podium rewards",
    subheading: "Each podium finisher earns a permanent badge for their trophy case.",
    footnote: "1st place also earns 1,000 QUSHi Coins.",
  },
};

/**
 * The whole card: heading, the three badges, and the line under them.
 * Used by the create wizard and by every landing page.
 */
// `surface` picks the client's copy set — "creation" is the default because the
// create wizard and the in-league League details screen both describe what the
// league awards, while the three landing pages ask for "Build your case".
// Individual strings can still be overridden; the public landing page, for one,
// keeps its heading colour from an earlier client revision.
export const PodiumRewardsCard = ({
  variantKey = "public",
  className = "",
  tinted = false,
  surface = "creation",
  heading,
  subheading,
  subheadingDesktop,
  footnote,
  headingColor = "#1A1A1A",
}) => {
  const copy = PODIUM_COPY[surface] || PODIUM_COPY.creation;
  const sub = subheading || copy.subheading;

  // Client: "Apply it to the three podium badges on public leagues, private
  // leagues, and Cash Game landing pages." Those three are exactly the surfaces
  // that take the "landing" copy set, so the tap-to-enlarge follows `surface`
  // rather than a fourth prop each landing page would have to remember to pass.
  // The create wizard keeps plain, untappable art: nothing has been won there
  // yet, and the client scoped this to the landing pages.
  const [openKey, setOpenKey] = useState(null);
  const openBadge = useCallback((key) => setOpenKey(key), []);
  const closeBadge = useCallback(() => setOpenKey(null), []);
  const enlargeable = surface === "landing";
  const openedBadge = PODIUM_BADGES.find((b) => b.key === openKey) || null;

  return (
    <div
      className={`rounded-2xl border border-gray-200 p-4 md:p-5 ${
        tinted ? "bg-[#F8FAFA]" : "bg-white"
      } ${className}`}
    >
      <h4 className="font-montserrat font-bold text-base" style={{ color: headingColor }}>
        {heading || copy.heading}
      </h4>
      {/* `subheadingDesktop` is a per-breakpoint subtitle, and only the public
          league landing page asks for one — client: "PUBLIC LEAGUE Landing page:
          DESKTOP VERSION only. We have room for this on this particular screen.
          Please change the subtitle: A top 3 finish adds a badge to your trophy
          case."

          Both strings are rendered and CSS picks one, the same way ASSET TYPE is
          handled on the cards above. Choosing in JS off a width would mean the
          wrong sentence on the first paint and a second one after a resize
          listener fires; this way the sentence can never disagree with the width
          it is being read at. */}
      <p className="mt-0.5 text-sm text-secondary">
        {subheadingDesktop ? (
          <>
            <span className="md:hidden">{sub}</span>
            <span className="hidden md:inline">{subheadingDesktop}</span>
          </>
        ) : (
          sub
        )}
      </p>

      {/* Client (badge asset drop): "Try to make the badges as big as possible
          within the allotted space."

          A three-column grid is what makes that possible: the COLUMNS take the
          card's full inner width, the gutters are equal by construction, and each
          badge fills its column edge to edge. The card keeps its own p-4 / md:p-5
          padding, so nothing runs to the card's edge — the client asked for normal
          side padding to be preserved in an earlier revision.

          There is deliberately no max-width on a badge any more. The previous art
          was 800px and a cap stopped a wide desktop card inflating it past the size
          it was drawn at; the new art is 1254px, which is larger than the column
          ever gets, so the cap now only makes the badges smaller than the space
          allows. The gutter is the smallest that still reads as three separate
          badges, for the same reason. */}
      <div className="mt-4 grid grid-cols-3 gap-2 md:gap-3 items-start">
        {PODIUM_BADGES.map((badge) => (
          <PodiumBadge
            key={badge.key}
            badgeKey={badge.key}
            caption={badge.caption}
            placement={badge.placement}
            variantKey={variantKey}
            onOpen={enlargeable ? openBadge : undefined}
          />
        ))}
      </div>

      <p className="mt-4 pt-4 border-t border-divider text-sm text-secondary">
        {footnote || copy.footnote}
      </p>

      {enlargeable && (
        <PodiumBadgeLightbox
          variantKey={variantKey}
          badge={openedBadge}
          onClose={closeBadge}
        />
      )}
    </div>
  );
};

export default PodiumRewardsCard;
